import GetAppIcon from "@mui/icons-material/GetApp";
import InfoIcon from "@mui/icons-material/Info";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import Document from "documents/document";
import { DocumentFileType } from "documents/document-file-type";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import React from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
import WorkDocument from "work/work-agreements/contracts/work-document";

const DocumentList = styled(List)(() => ({
  "&.MuiList-root": {
    marginLeft: 0,
  },
  maxWidth: "42rem",
  /**
   * A floor that yields once there is no room for it.
   *
   * A flat `32rem` is 512px, which is wider than a phone — and wider than the archived Conflicts and
   * Policies tabs are given on one — so the list overflowed its own tab and a reader had to scroll
   * sideways to see a document's date. `min()` keeps the floor wherever the room exists and drops to
   * the room available where it does not, which is the same thing the row's own columns do.
   */
  minWidth: "min(32rem, 100%)",
  width: "100%",
}));
const ListSubhead = styled(ListSubheader)(() => ({
  backgroundColor: "#FFF",
  color: "#000",
  display: "flex",
  fontSize: "1.4em",
  justifyContent: "space-between",
  paddingRight: 0,
  whiteSpace: "nowrap",
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.3em",
  paddingTop: theme.spacing(1),
}));
const DocumentIcon = styled(ListItemAvatar)(({ theme }) => ({
  marginRight: theme.spacing(1),
  minWidth: "2rem",
  maxWidth: "2rem",
}));

type DocumentListReadonlyProps = {
  className?: string;
  documents?: WorkDocument[];
  documentType: WorkDocumentType;
  downloadingFileId: Guid | null;
  conflictsCheckWaived?: boolean;
  onDownload: (documentId: Guid) => void;
};

export default function DocumentListReadonly(props: Readonly<DocumentListReadonlyProps>) {
  const {
    className,
    documents,
    documentType,
    downloadingFileId,
    conflictsCheckWaived,
    onDownload,
  } = props;

  function getPolicyHeader(): string {
    return documentType === WorkDocumentType.VendorPolicy ? "Vendor Policies" : "Client Policies";
  }

  function renderActionButtons(document: WorkDocument) {
    const isUnavailable = document.isAvailable === false;

    return (
      <>
        <Tooltip title={isUnavailable ? "Document unavailable" : "Download Document"}>
          <span>
            <IconButton
              edge="end"
              disabled={downloadingFileId !== null || isUnavailable}
              onClick={() => onDownload(document.id)}
              size="large"
            >
              {downloadingFileId?.isEqualTo(document.id) ? (
                <CircularProgress color="primary" size={28} disableShrink thickness={4} />
              ) : (
                <GetAppIcon color={isUnavailable ? "disabled" : "primary"} fontSize="medium" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  }

  return (
    <DocumentList disablePadding className={className}>
      <ListSubhead>
        {documentType === WorkDocumentType.Conflicts && (
          <span>
            Your Conflicts Checks
            <Tooltip
              disableFocusListener
              title="Conflicts document(s) establish potential conflicts checks required by participating clients/vendors"
              placement="bottom"
              arrow
            >
              <IconButton size="large" edge="end">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </span>
        )}
        {(documentType === WorkDocumentType.ClientPolicy || documentType === WorkDocumentType.VendorPolicy) && (
          <span>
            {getPolicyHeader()}
            <Tooltip
              disableFocusListener
              title="Policy document(s) establish the policies required by participating clients/vendors"
              placement="bottom"
              arrow
            >
              <IconButton size="large" edge="end">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </span>
        )}
      </ListSubhead>
      {!conflictsCheckWaived && (!documents || documents.length < 1) && (
        <ListItem key="noDocuments">
          {/* The icon spacer keeps the empty state lined up with the document rows rather than the
                section heading. */}
          <DocumentIcon />
          <NoRowsPlaceholder>No Documents</NoRowsPlaceholder>
        </ListItem>
      )}
      {!conflictsCheckWaived &&
        documents
          ?.filter((document) => !(document instanceof UnavailableDocument))
          .map((document: WorkDocument | UnavailableDocument) => {
            const availableDocument = document as WorkDocument;
            const secondaryText = availableDocument.isAvailable === false
              ? "Unavailable"
              : availableDocument.created?.format("MM/DD/YY hh:mm A");
            return (
              <ListItem key={document.id.value as React.Key} secondaryAction={renderActionButtons(availableDocument)}>
                <DocumentIcon>
                  <FileIcon {...getFileTypeIconProps(availableDocument)} />
                </DocumentIcon>
                <ListItemText
                  primary={availableDocument.name?.value}
                  secondary={secondaryText}
                  sx={{ marginRight: 6 }}
                  slotProps={{
                    primary: {
                      sx: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }
                    },
                  }}
                />
              </ListItem>
            );
          })}
    </DocumentList>
  );
}

/** Only ever called for a row the list kept, and the list filters `UnavailableDocument` out. */
function getFileTypeIconProps(document: Document) {
  const extension = enumKeyOf(DocumentFileType, document.fileType).toLocaleLowerCase();

  return {
    extension,
    ...defaultStyles[extension as keyof typeof defaultStyles],
  };
}
